/**
 * useCalibrationSwitcherState Hook
 * 
 * Manages the "Switch to Calibration Metrics" button enabled/disabled state.
 * The button should be disabled when already in calibration mode.
 */

import { useState, useCallback, useEffect } from 'react';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';
import {
  calcIsDataBrainCalibrationMetricsOnly,
  calcIsSegmentForSalesAllChecked,
  getAllValuesOfCascader,
} from '../utils';

interface UseCalibrationSwitcherStateParams {
  filterValue: any;
  defaultInputList: any[];
}

interface UseCalibrationSwitcherStateResult {
  isDisabled: boolean;
  updateDisabledState: (filterValue: any) => void;
}

/**
 * Hook to manage calibration switcher button state
 * 
 * The button is disabled when:
 * - Date type is 'date' (daily granularity)
 * - AND metrics are only DataBrain calibration metrics
 * - AND all segments are selected
 * 
 * @param filterValue - Current filter state
 * @param defaultInputList - Filter input configuration list
 * @returns Disabled state and update function
 * 
 * @example
 * ```typescript
 * const { isDisabled, updateDisabledState } = useCalibrationSwitcherState({
 *   filterValue,
 *   defaultInputList
 * });
 * ```
 */
export function useCalibrationSwitcherState(
  params: UseCalibrationSwitcherStateParams
): UseCalibrationSwitcherStateResult {
  const { filterValue, defaultInputList } = params;
  const [isDisabled, setIsDisabled] = useState(false);

  const updateDisabledState = useCallback(
    (currentFilterValue: any) => {
      // Check date type - if not 'date', enable the button
      if (currentFilterValue?.[filterKeyMap.RANGE_TIME]?.type !== 'date') {
        setIsDisabled(false);
        return;
      }

      // Check metrics - if not only calibration metrics, enable the button
      const metrics =
        currentFilterValue?.[filterKeyMap.METRIC_SELECT_FOR_KPI_TREND]?.value?.metrics;
      if (!calcIsDataBrainCalibrationMetricsOnly(metrics)) {
        setIsDisabled(false);
        return;
      }

      // Check segments - if not all selected, enable the button
      const cascaderConfig = defaultInputList.find(
        (item) => item?.key === filterKeyMap.CASCADER
      );
      const cascaderOptions = (cascaderConfig?.options as any)?.children || [];
      
      if (
        !calcIsSegmentForSalesAllChecked(
          cascaderOptions,
          currentFilterValue?.[filterKeyMap.CASCADER]?.value
        )
      ) {
        setIsDisabled(false);
        return;
      }

      // All conditions met - disable the button (already in calibration mode)
      setIsDisabled(true);
    },
    [defaultInputList]
  );

  // Auto-update when filterValue changes
  useEffect(() => {
    updateDisabledState(filterValue);
  }, [filterValue, updateDisabledState]);

  return { isDisabled, updateDisabledState };
}
